/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable no-unused-vars */

import type { Dispatch, ReactElement, SetStateAction } from 'react';
import React, { useState } from 'react';
import cx from 'classnames';
import type { PickerItems } from '../../atoms';
import { InputField, Label, PickerInput, RadioButton, SubmitButton } from '../../atoms';

const questionContainerClasses = cx('flex', 'flex-column', 'mb-12');
const questionLabelClasses = cx('mb-4');
const radioRowContainerClasses = cx('flex', 'flex-start', 'gap-2', 'mt-4');
const radioColumnContainerClasses = cx('flex', 'flex-column', 'gap-2', 'flex-space-around', 'mt-4');
const buttonContainerClasses = cx('flex', 'flex-center', 'mt-20', 'mb-16');

type CheckboxFieldProps<T> = {
  name: keyof T & string;
  type: 'checkbox';
  title?: string;
};

type RadiosFieldProps<T> = {
  name: keyof T & string;
  type: 'radios';
  options?: ReadonlyArray<string>;
  title?: string;
  /** Show an 'other' text box if they select a specified one of the options. */
  otherOption?: string;
};

type TextFieldProps<T> = {
  name: keyof T & string;
  type: 'text';
  placeholder?: string;
  title?: string;
  inputType?: 'text' | 'date' | 'email' | 'number';
};

type PickerFieldProps<T> = {
  name: keyof T & string;
  type: 'picker';
  title?: string;
  items: PickerItems;
};

export type FieldProps<T> = RadiosFieldProps<T> | TextFieldProps<T> | CheckboxFieldProps<T> | PickerFieldProps<T>;

export type FieldsProps<T> = ReadonlyArray<FieldProps<T>>;

type FieldsSetter<T> = Dispatch<SetStateAction<Partial<T>>>;

export function CheckboxField<T>({
  setData,
  name,
  title,
  value: initialValue,
}: { setData: FieldsSetter<T>; value?: unknown } & CheckboxFieldProps<T>): ReactElement {
  const [checked, setChecked] = useState<boolean | undefined>(
    initialValue === true ? true : initialValue === false ? false : undefined
  );
  const handleChange = (value: boolean) => {
    setChecked(value);
    setData((data) => ({ ...data, [name]: value }));
  };

  return (
    <div className={questionContainerClasses}>
      {title && <Label className={questionLabelClasses}>{title}</Label>}
      <div className={radioRowContainerClasses}>
        <RadioButton //
          id={`${name}_true`}
          name={name}
          value='true'
          checked={checked === true}
          onChange={() => handleChange(true)}
        >
          Ja
        </RadioButton>
        <RadioButton
          id={`${name}_false`}
          name={name}
          value='false'
          checked={checked === false}
          onChange={() => handleChange(false)}
        >
          Nein
        </RadioButton>
      </div>
    </div>
  );
}

export function TextField<T>({
  setData,
  name,
  title,
  placeholder,
  inputType = 'text',
  value: initialValue,
}: { setData: FieldsSetter<T>; value?: unknown } & TextFieldProps<T>): ReactElement {
  const [value, setValue] = useState(typeof initialValue === 'string' ? initialValue : '');

  return (
    <div className={questionContainerClasses}>
      {title && <Label className={questionLabelClasses}>{title}</Label>}
      <InputField
        type={inputType}
        value={value}
        onChange={(newValue) => {
          setValue(newValue);
          setData((data) => ({ ...data, [name]: newValue }));
        }}
        placeholder={placeholder}
      />
    </div>
  );
}

export function RadiosField<T>({
  setData,
  name,
  title,
  options = [],
  otherOption = '',
  value,
}: { setData: FieldsSetter<T>; value?: unknown } & RadiosFieldProps<T>): ReactElement {
  const [selected, setSelected] = useState(
    typeof value === 'string' ? (options.includes(value) ? value : otherOption) : ''
  );
  const [otherValue, setOtherValue] = useState(typeof value === 'string' && !options.includes(value) ? value : '');

  const showOther = otherOption && selected === otherOption;
  const handleRadioChange = (newValue: string) => {
    setSelected(newValue);
    if (newValue !== otherOption) {
      setData((data) => ({ ...data, [name]: newValue }));
    } else {
      setData((data) => ({ ...data, [name]: otherValue }));
    }
  };

  return (
    <div className={questionContainerClasses}>
      {title && <Label className={questionLabelClasses}>{title}</Label>}
      <div className={options.length > 3 ? radioColumnContainerClasses : radioRowContainerClasses}>
        {options.map((opt) => (
          <div key={opt}>
            <RadioButton
              id={`${name}_${opt}`}
              name={name}
              value={opt}
              defaultChecked={selected === opt}
              onChange={() => handleRadioChange(opt)}
            >
              {opt}
            </RadioButton>
          </div>
        ))}
      </div>
      {showOther && (
        <InputField
          value={otherValue}
          onChange={(newValue) => {
            setOtherValue(newValue);
            setData((data) => ({ ...data, [name]: newValue }));
          }}
          placeholder='Bitte angeben...'
        />
      )}
    </div>
  );
}

export function PickerField<T>({
  name,
  title,
  items,
  setData,
  value: initialValue,
}: { setData: FieldsSetter<T>; value?: unknown } & PickerFieldProps<T>): ReactElement {
  const [value, setValue] = useState(typeof initialValue === 'string' ? initialValue : '');

  return (
    <div className={questionContainerClasses}>
      {title && <Label className={questionLabelClasses}>{title}</Label>}
      <PickerInput
        disableFirstLabel={false}
        items={items}
        value={value}
        onChange={(newValue) => {
          setValue(newValue);
          setData((data) => ({ ...data, [name]: newValue }));
        }}
      />
    </div>
  );
}

export function Fields<T>({
  fields,
  data = {},
  setData,
}: {
  fields: FieldsProps<T>;
  data: Partial<T>;
  setData: FieldsSetter<T>;
}): ReactElement {
  return (
    <>
      {fields.map((field) => {
        switch (field.type) {
          case 'checkbox':
            return <CheckboxField key={field.name} value={data[field.name]} {...field} setData={setData} />;
          case 'radios':
            return <RadiosField key={field.name} value={data[field.name]} {...field} setData={setData} />;
          case 'text':
            return <TextField key={field.name} value={data[field.name]} {...field} setData={setData} />;
          case 'picker':
            return <PickerField key={field.name} value={data[field.name]} {...field} setData={setData} />;
          default:
            return null;
        }
      })}
    </>
  );
}

export function Form<T>({
  fields,
  data: initialData = {},
  onSubmit,
  button = 'Weitermachen',
  disabled = false,
}: {
  fields: FieldsProps<T>;
  data?: Partial<T>;
  onSubmit: (data: T) => void;
  button?: string;
  disabled?: boolean;
}): ReactElement {
  const [data, setData] = useState<Partial<T>>(initialData);

  // Check if all required fields are filled
  const allFieldsFilled = fields.every((field) => typeof data[field.name] !== 'undefined' && data[field.name] !== '');

  return (
    <form
      style={{ margin: '0 auto', maxWidth: '500px' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (allFieldsFilled && !disabled) {
          onSubmit(data as T);
        }

        return false;
      }}
    >
      <Fields fields={fields} data={data} setData={setData} />
      <div className={buttonContainerClasses}>
        <SubmitButton disabled={!allFieldsFilled || disabled}>{button}</SubmitButton>
      </div>
    </form>
  );
}
